/**
 * Join step one: who the participant is and where they depart from.
 *
 * The normal path is picking a departure point by name, because a group meets at
 * a place rather than at a pair of numbers. Searching reads what is already stored
 * and costs nothing; when that finds nothing, which is every search on a fresh
 * deployment, the same query falls through to the online lookup by itself instead
 * of asking for a second click. Typing coordinates stays available as the escape
 * hatch for a member who wants their exact spot.
 */

import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'

import { isPublicAnchorKind } from '@shared/anchors'
import { LIMITS } from '@shared/constants'
import type { DepartureAnchorDto } from '@shared/types'

import { ErrorAlert } from '../../../components/layout/states'
import { WizardFooter } from '../../../components/layout/wizard'
import { Alert, AlertDescription } from '../../../components/ui/alert'
import { Button } from '../../../components/ui/button'
import { Card, CardContent } from '../../../components/ui/card'
import { Chip, ChipGroup } from '../../../components/ui/chip'
import { Field, FieldGroup } from '../../../components/ui/field'
import { Input } from '../../../components/ui/input'
import { useDepartureAnchors, useDepartureLookup } from '../../../hooks/use-anchors'
import { useMyParticipant } from '../../../hooks/use-shared'
import { hasDeparture } from '../../../stores/drafts'
import { useSharedOutlet } from '../shared-layout'
import { joinStepNeighbours, joinStepPath, useJoinDraft } from './layout'

interface LocationErrors {
  nickname?: string
  locationLabel?: string
  /** What is wrong with the departure point itself, whichever way it was named. */
  departure?: string
}

/**
 * How long a local miss waits before the online search runs.
 *
 * The query changes on every keystroke and a provider call per keystroke would be
 * both wasteful and a fight with the lookup limit, so the fallback rides the pause
 * after typing.
 */
const REMOTE_SEARCH_DELAY_MS = 450

/** Parses a decimal input, treating an empty or unusable value as missing. */
function parseCoordinate(value: string): number | null {
  if (value.trim() === '') {
    return null
  }
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function formatCoordinate(value: number | null): string {
  return value === null ? '' : String(value)
}

export function JoinLocationPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { shareToken } = useSharedOutlet()
  const { draft, update } = useJoinDraft(shareToken)
  const { next } = joinStepNeighbours('location')
  // An existing participation may be edited without naming a departure point
  // again; this distinguishes that case from a first join, which needs one.
  const participantQuery = useMyParticipant(shareToken)
  const isEdit = Boolean(participantQuery.data)

  const [errors, setErrors] = useState<LocationErrors>({})
  const [query, setQuery] = useState('')
  const [search, setSearch] = useState('')
  const [remote, setRemote] = useState<DepartureAnchorDto[]>([])
  // Raw coordinates are the escape hatch, not the default: the fields stay
  // collapsed until the member asks for them or a stored draft already has them.
  const [exact, setExact] = useState(
    () => draft.locationLat !== null || draft.locationLng !== null,
  )
  // The coordinate inputs keep their own text: parsing on every keystroke would
  // turn "31." into 31 and make a decimal point impossible to type.
  const [latText, setLatText] = useState(() => formatCoordinate(draft.locationLat))
  const [lngText, setLngText] = useState(() => formatCoordinate(draft.locationLng))

  const anchorsQuery = useDepartureAnchors(search)
  const lookup = useDepartureLookup()
  const { mutate: lookUp } = lookup
  /** Query the online search was already fired for, so a miss is retried only once. */
  const askedRemotely = useRef<string | null>(null)

  const remembered = anchorsQuery.data?.remembered ?? []
  const local = anchorsQuery.data?.anchors ?? []
  const options = remote.length > 0 ? remote : local
  const trimmedQuery = query.trim()
  const localSettled = anchorsQuery.isSuccess || anchorsQuery.isError
  // A local miss is the normal case, not an error: the table only holds the places
  // somebody already resolved, so a new deployment has nothing to match. The online
  // search takes over instead of the member having to press a second button.
  const fallingBackToRemote =
    trimmedQuery !== '' &&
    localSettled &&
    local.length === 0 &&
    remote.length === 0 &&
    askedRemotely.current !== trimmedQuery

  useEffect(() => {
    // Adopt a coordinate that changed outside these inputs (a departure pick or
    // the server seed) without clobbering a half-typed number.
    if (parseCoordinate(latText) !== draft.locationLat) {
      setLatText(formatCoordinate(draft.locationLat))
    }
    if (parseCoordinate(lngText) !== draft.locationLng) {
      setLngText(formatCoordinate(draft.locationLng))
    }
  }, [draft.locationLat, draft.locationLng, latText, lngText])

  useEffect(() => {
    if (!fallingBackToRemote || lookup.isPending) {
      return
    }
    const handle = setTimeout(() => {
      askedRemotely.current = trimmedQuery
      lookUp(trimmedQuery, { onSuccess: (result) => setRemote(result.anchors) })
    }, REMOTE_SEARCH_DELAY_MS)
    return () => clearTimeout(handle)
  }, [fallingBackToRemote, lookup.isPending, trimmedQuery, lookUp])

  // A departure point is shown to every member of the event, which is worth
  // saying out loud when the member picked a private place or their own spot.
  const picked = options.find((anchor) => anchor.id === draft.anchorId)
    ?? remembered.find((anchor) => anchor.id === draft.anchorId)
  const privateDeparture =
    draft.anchorId === null
      ? draft.locationLat !== null && draft.locationLng !== null
      : picked !== undefined && !isPublicAnchorKind(picked.kind)

  function pick(anchor: DepartureAnchorDto): void {
    update({
      anchorId: anchor.id,
      // A named place and a shared group are alternatives: picking one is a
      // statement about where this member starts, so an earlier group choice is
      // dropped instead of silently winning on the server.
      travelGroupId: null,
      locationLabel: anchor.name,
      locationLat: null,
      locationLng: null,
    })
    setExact(false)
    setLatText('')
    setLngText('')
    setErrors((current) => ({ ...current, locationLabel: undefined, departure: undefined }))
  }

  function lookUpRemotely(): void {
    const value = query.trim()
    if (value === '') {
      return
    }
    setSearch(value)
    setRemote([])
    // Pressing the button is an explicit retry, so it runs even for a query the
    // fallback already tried.
    askedRemotely.current = value
    lookUp(value, { onSuccess: (result) => setRemote(result.anchors) })
  }

  function useExactPosition(): void {
    setExact(true)
    // Coordinates and a picked place are alternatives, so the picked anchor, any
    // group choice and the name of that place are all cleared: the point the member
    // types has to be stored under a name that describes it, not one inherited from
    // the place they abandoned.
    update({ anchorId: null, travelGroupId: null, locationLabel: '' })
  }

  /** Hides the coordinate fields and drops whatever was typed into them. */
  function closeExactPosition(): void {
    setExact(false)
    setLatText('')
    setLngText('')
    update({ locationLat: null, locationLng: null })
  }

  /**
   * Whether the name field is required.
   *
   * A first join always names its departure, and a member typing coordinates
   * names the spot those numbers point at. A picked place carries its own name,
   * and an edit may keep the group it already has.
   */
  const labelRequired = draft.anchorId === null && (draft.locationLat !== null || !isEdit)

  function goNext(): void {
    const nextErrors: LocationErrors = {}
    if (draft.nickname.trim() === '') {
      nextErrors.nickname = t('join.nicknameRequired')
    }
    // A first join must name where it starts from; an edit may keep whatever the
    // registration already has, including nothing. The message names the actual
    // problem: only coordinates the member typed can be rejected for their range,
    // and a local search that found nothing is not a coordinate problem at all.
    if (!hasDeparture(draft) && !isEdit) {
      const typedSomething = latText.trim() !== '' || lngText.trim() !== ''
      nextErrors.departure =
        draft.locationLat !== null && draft.locationLng !== null
          ? t('join.coordinateError')
          : typedSomething
            ? t('join.coordinatePairRequired')
            : t('join.departureRequired')
    }
    if (labelRequired && draft.locationLabel.trim() === '') {
      nextErrors.locationLabel = t('join.locationRequired')
    }

    setErrors(nextErrors)
    if (Object.values(nextErrors).some((message) => message !== undefined) || !next) {
      return
    }
    navigate(joinStepPath(shareToken, next))
  }

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardContent className="flex flex-col gap-5 pt-4 sm:pt-5">
          <FieldGroup>
            <Field
              label={t('join.nicknameLabel')}
              htmlFor="nickname"
              required
              hint={t('join.nicknamePlaceholder')}
              error={errors.nickname}
            >
              <Input
                id="nickname"
                name="nickname"
                value={draft.nickname}
                maxLength={LIMITS.nicknameMaxLength}
                aria-invalid={Boolean(errors.nickname)}
                onChange={(event) => update({ nickname: event.target.value })}
              />
            </Field>

            <Field
              label={t('join.headcountLabel')}
              htmlFor="headcount"
              hint={t('join.headcountHint')}
            >
              <Input
                id="headcount"
                name="headcount"
                type="number"
                min={1}
                max={LIMITS.maxHeadcountPerRegistration}
                value={String(draft.headcount)}
                onChange={(event) => {
                  const parsed = Number(event.target.value)
                  update({
                    headcount: Number.isFinite(parsed)
                      ? Math.min(Math.max(1, Math.trunc(parsed)), LIMITS.maxHeadcountPerRegistration)
                      : 1,
                  })
                }}
              />
            </Field>

            <Field
              label={t('join.locationLabel')}
              htmlFor="locationLabel"
              required={labelRequired}
              hint={t('join.locationPlaceholder')}
              error={errors.locationLabel}
            >
              <Input
                id="locationLabel"
                name="locationLabel"
                value={draft.locationLabel}
                maxLength={LIMITS.locationLabelMaxLength}
                aria-invalid={Boolean(errors.locationLabel)}
                onChange={(event) => update({ locationLabel: event.target.value })}
              />
            </Field>
          </FieldGroup>

          <div className="flex flex-col gap-2">
            <h2 className="text-sm font-medium">{t('join.departureSearchLabel')}</h2>
            <div className="flex gap-2">
              <Input
                id="departureQuery"
                name="departureQuery"
                value={query}
                aria-label={t('join.departureSearchLabel')}
                placeholder={t('join.departureSearchPlaceholder')}
                onChange={(event) => {
                  setQuery(event.target.value)
                  setSearch(event.target.value)
                  setRemote([])
                }}
              />
              <Button type="button" variant="outline" onClick={lookUpRemotely} disabled={lookup.isPending}>
                {t('join.departureSearchRemote')}
              </Button>
            </div>
            {/*
              The search box, the name field and the remembered places used to be
              unlabelled relative to each other, so a member typed into the wrong
              one. The hint says what each does and what the online button costs.
            */}
            <p className="text-muted-foreground text-xs">{t('join.departureSearchHint')}</p>

            {remembered.length > 0 && remote.length === 0 ? (
              <div className="flex flex-col gap-1">
                <span className="text-muted-foreground text-xs">{t('join.departureRemembered')}</span>
                <ChipGroup label={t('join.departureRemembered')}>
                  {remembered.map((anchor) => (
                    <Chip
                      key={anchor.id}
                      selected={draft.anchorId === anchor.id}
                      onClick={() => pick(anchor)}
                    >
                      {anchor.name}
                    </Chip>
                  ))}
                </ChipGroup>
              </div>
            ) : null}

            {options.length > 0 ? (
              <ChipGroup label={t('join.departureTitle')}>
                {options.map((anchor) => (
                  <Chip
                    key={anchor.id}
                    selected={draft.anchorId === anchor.id}
                    onClick={() => pick(anchor)}
                  >
                    {anchor.name}
                  </Chip>
                ))}
              </ChipGroup>
            ) : (
              <p className="text-muted-foreground text-xs">
                {trimmedQuery === ''
                  ? t('join.departureEmpty')
                  : lookup.isPending || fallingBackToRemote
                    ? t('join.departureSearchingRemote')
                    : t('join.departureNoMatch')}
              </p>
            )}

            {lookup.error ? (
              <ErrorAlert message={t('join.departureLookupFailed')} />
            ) : null}
            <p className="text-muted-foreground text-xs">{t('join.departureHint')}</p>
          </div>

          <div className="flex flex-col gap-2">
            {exact ? (
              <FieldGroup>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-sm font-medium">{t('join.exactPositionTitle')}</span>
                  <Button type="button" variant="ghost" size="sm" onClick={closeExactPosition}>
                    {t('common.cancel')}
                  </Button>
                </div>
                <Field
                  label={t('join.locationLatLabel')}
                  htmlFor="locationLat"
                  hint={t('join.coordinateHint')}
                >
                  <Input
                    id="locationLat"
                    name="locationLat"
                    inputMode="decimal"
                    value={latText}
                    aria-invalid={Boolean(errors.departure)}
                    onChange={(event) => {
                      setLatText(event.target.value)
                      update({ locationLat: parseCoordinate(event.target.value), anchorId: null })
                    }}
                  />
                </Field>

                <Field label={t('join.locationLngLabel')} htmlFor="locationLng">
                  <Input
                    id="locationLng"
                    name="locationLng"
                    inputMode="decimal"
                    value={lngText}
                    aria-invalid={Boolean(errors.departure)}
                    onChange={(event) => {
                      setLngText(event.target.value)
                      update({ locationLng: parseCoordinate(event.target.value), anchorId: null })
                    }}
                  />
                </Field>
              </FieldGroup>
            ) : (
              <Button type="button" variant="ghost" size="sm" onClick={useExactPosition}>
                {t('join.useExactPosition')}
              </Button>
            )}
          </div>

          {errors.departure ? (
            <ErrorAlert message={errors.departure} />
          ) : null}

          {privateDeparture ? (
            <Alert variant="info">
              <AlertDescription>{t('join.departurePrivateHint')}</AlertDescription>
            </Alert>
          ) : null}
        </CardContent>
      </Card>

      <WizardFooter
        backTo={`/e/${shareToken}`}
        backLabel={t('common.back')}
        onNext={goNext}
        nextLabel={t('common.next')}
      />
    </div>
  )
}
